import { useState, useEffect, useCallback, useRef } from 'react'
import { useSsoToken } from './useSsoToken'
import { getRepliconUserStatus } from '../services/claimsApi'

// Whether the signed-in user has a Replicon account, which decides what the Time Entry tab
// shows: the booking form, or a link to Vision for anyone who could not book time. Asked only
// when `enabled` (the EnableTimeEntry flag) is on: with the flag off the tab is hidden
// regardless, and the endpoint would answer 403. App is the only consumer, so this is one
// request per add-in session.
//
// A failure is not a "no". The backend errors rather than answering false when Replicon is
// down, and any failure here (Replicon, the API, the token) leaves the answer unknown — so
// `unavailable` is set and the tab says Time Entry is unavailable, rather than sending a
// Replicon user to Vision. Unlike a flags failure this does not block the pane.
export function useRepliconUserStatus(enabled: boolean) {
  const { getToken } = useSsoToken()
  const [isRepliconUser, setIsRepliconUser] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  // Whether the latest answer (or failure) is in, so the tab can wait for it rather than show
  // the Vision link to a Replicon user and then swap it for the form.
  const [checked, setChecked] = useState(false)
  // Set on the first request and never cleared, so the API is asked at most once per session
  // however often `enabled` flips. Only an explicit `retry` asks again.
  const requested = useRef(false)

  const load = useCallback(async () => {
    setChecked(false)
    try {
      const token = await getToken()
      setIsRepliconUser(await getRepliconUserStatus(token))
      setUnavailable(false)
    } catch (err: unknown) {
      console.error('[RepliconUserStatus] Failed to load:', err)
      setIsRepliconUser(false)
      setUnavailable(true)
    } finally {
      setChecked(true)
    }
  }, [getToken])

  useEffect(() => {
    if (!enabled || requested.current) return
    requested.current = true
    // Next tick, so the load never calls setState directly inside the effect body.
    void Promise.resolve().then(load)
  }, [enabled, load])

  return {
    // Gated on `enabled` too, so nothing downstream sees a Replicon answer while the flag is off.
    isRepliconUser: enabled && isRepliconUser,
    unavailable: enabled && unavailable,
    loading: enabled && !checked,
    retry: load,
  }
}
